import { claimBuddyConversation } from 'config/buddy/conversations';
import { isOnboardingConversationId } from 'features/onboarding/onboarding-script';
import { ConfigurationSettings } from "/models/configuration-settings";
import { clients } from "lib/https";

/** True when the active chat is temporary/incognito (no conversation details API). */
export const isIncognitoConversationDetailsBlocked = (
  isIncognitoExplicit?: boolean,
): boolean => {
  if (typeof isIncognitoExplicit === 'boolean') return isIncognitoExplicit;
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('incognito-chat') === 'true';
};


export interface UpdateConversationPayload {
  conversationId: string;
    conversationTitle: string;
    isArchived?: boolean;
    projectId?: string;
  }

export interface getConversationByProjectIdPayload {
  "projectId": string,
  "pageSize": number,
  "page": number
}

  
  export interface UpdateConversationResponse {
    success: boolean;
    message?: string;
    data?: any;
    conversationId?: string;
  }

export interface ConversationEvent {
  createdDate: string;
  eventType: 'USER_ADDED' | 'USER_REMOVED' | 'CONVERSATION_TITLE_GENERATED' | 'CONVERSATION_TITLE_CREATED' | 'CONVERSATION_TITLE_UPDATED' | 'ASSISTANT_ADDED' | 'ASSISTANT_REMOVED' | 'CONVERSATION_CREATED';
  actorId: string;
  targetIds: string[];
}

export interface ShareMetadataResponse {
  token: string,
  permissionType: string,
  createdBy: string,
  createdDate: string,
  expiresAt: string
}

export interface ConversationResponse {
  isDashboard?: boolean;
  tenantId: string;
  conversationId: string;
  conversationTitle: string;
  createdBy: string;
  createdDate: string;
  isArchived: boolean;
  lastUpdatedDate: string;
  participants: Record<string, any>;
  messages: any[];
  events: ConversationEvent[];
  projectId: string | null;
  shareMetadata: ShareMetadataResponse[];
  customGptId?: string;
}
  
/**
 * Updates a collaboration conversation with new title, archive status, and project ID.
 *
 * @param {string} conversationId - The ID of the conversation to update.
 * @param {UpdateConversationPayload} payload - The update payload containing title, archive status, and project ID.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<UpdateConversationResponse>} The response from the collaboration API.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await updateConversationById('conv123', {
 *   conversationTitle: 'New Project Discussion',
 *   isArchived: false,
 *   projectId: 'proj456'
 * }, config);
 */

/**
 * Retrieves a collaboration conversation by its ID.
 *
 * @param {string} conversationId - The ID of the conversation to retrieve.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<ConversationResponse>} The conversation data including participants, messages, and events.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const conversation = await getConversationById('dd00a81d-413a-484d-8def-e3a2d57c2357', config);
 * console.log(conversation.conversationTitle); // "hi"
 */
export const getConversationById = async (
  conversationId: string,
  config: ConfigurationSettings,
  options?: { isIncognito?: boolean },
): Promise<ConversationResponse> => {
  if (isIncognitoConversationDetailsBlocked(options?.isIncognito)) {
    throw new Error('Incognito chats do not have stored conversations');
  }
  if (isOnboardingConversationId(conversationId)) {
    throw new Error('Onboarding chats are not stored conversations');
  }
  if (claimBuddyConversation(conversationId)) {
    throw new Error('Buddy replies are not stored conversations');
  }

  const baseUrl = `${config.paths.seliseAiBaseUrl}`;
  const url = `${baseUrl}${config.paths.seliseAi.collaborationConversation}/${conversationId}`;
  
  return clients.get<ConversationResponse>(url, config.blocksKey);
};

export const getConversationByProjectId = async (
    conversationId: string,
    payload: getConversationByProjectIdPayload,
    config: ConfigurationSettings
  ) => {

    type ApiConversationResponse = {
      tenantId: string;
      conversationId: string;
      conversationTitle: string;
      createdBy: string;
      createdDate: string;
      isArchived: boolean;
      lastUpdatedDate: string;
      participants: object;
      messages: any[];
      events: any[];
      projectId: string | null;
  };


      const baseUrl = `${config.paths.seliseAiBaseUrl}`;
      const url = `${baseUrl}${config.paths.seliseAi.collaborationConversation}`;
      const res = await clients.post<{ data: ApiConversationResponse[], total_count: number }>(
        `${url}`,
        config.blocksKey,
        JSON.stringify(payload)
      );

  };


  export interface UpdateConversationRequest {
    conversationId: string;
    conversationTitle?: string,
    isArchived?: boolean,
 }


  export const updateConversationById = async (configurationSettings: ConfigurationSettings, payload: UpdateConversationRequest) => {

    const Payload: any = { };
    payload.conversationTitle && (Payload["conversationTitle"] = payload.conversationTitle);
     "isArchived" in payload && (Payload["isArchived"] = payload.isArchived);
     
      const baseUrl = `${configurationSettings.paths.seliseAiBaseUrl}`;
      const url = `${baseUrl}${configurationSettings.paths.seliseAi.collaborationConversation}/${payload.conversationId}`;
      const response = await clients.put(url, configurationSettings.blocksKey, JSON.stringify(Payload));
      return response;
  }
  